/**
 * The session rules, mirrored on the client — and nowhere else in the UI.
 *
 * Every rule here is enforced by the server first. This module exists so the
 * screen can *offer the right thing* instead of offering everything and letting
 * a manager discover the refusal, and it is deliberately one file so there is one
 * place to check when the backend's rules move. Nothing here grants anything:
 *
 * - **Ownership.** `docs/spec.md:101` — a session locks to its responsible
 *   manager for entry, with **no admin bypass**. The server enforces it in
 *   `count/sessions.py` `_assert_owner` (403), and holding `can_enter_counts` is
 *   not permission to type into someone else's count. So the UI shows *View*
 *   rather than *Resume*, and the read-only screen quotes the rule.
 * - **Editability.** `EDITABLE_STATUSES` mirrors the AL staging table's own gate
 *   (`LSCountLine.Table.al` `TestSessionMutable`), which the backend mirrors too
 *   and pins with a test. Mirrored here for the same reason the backend mirrors
 *   it: an explanation beats a relayed BC error. `session.editable` is the
 *   server's own answer and is preferred whenever it is present.
 * - **Where a status opens.** A count that is posting belongs on the posting
 *   screen; a count that failed belongs on the review screen with BC's words in
 *   front of the manager, because the way back — submit for review again —
 *   **clears the error text** as the AL transitions it.
 *
 * **What is NOT here: a one-open-session rule.** Neither the AL extension nor
 * this app's backend defines one — `LSCountSession.Table.al` has no such
 * constraint, its key is the session id alone, and `create_session` refuses
 * nothing. Several `Open` sessions can therefore exist for one area at the same
 * time, owned by the same manager or by different ones, and each posts
 * separately. The UI does not invent the rule (a client-side block that no other
 * client honours is theatre), it *surfaces the state*: see
 * `startWouldBeConcurrent`.
 */
import type { CountSession } from "../api/types";

/**
 * Statuses in which staging accepts line changes.
 *
 * Mirrors `count/sessions.py` `EDITABLE_STATUSES`, which mirrors the AL. Pinned
 * against the backend by `tests/test_frontend_contract.py` so the two cannot
 * drift unnoticed in either direction.
 */
export const EDITABLE_STATUSES = ["Open", "InReview", "Error"] as const;

/** What "in progress" means. Mirrors `count/sessions.py` `ACTIVE_STATUSES`. */
export const ACTIVE_STATUSES = ["Open", "InReview", "Error", "Posting"] as const;

/** Where a session opens, and therefore which screen renders it. */
export type SessionRoute = "entry" | "review" | "posting" | "view";

export interface SessionAffordance {
  /** The route segment under `/areas/:areaId/sessions/:id/`. */
  route: SessionRoute;
  /** The button's words. Distinct per state, because the states differ. */
  label: string;
  /** Whether opening it can change anything. `false` for every non-owner path. */
  readOnly: boolean;
  /** Why this session opens where it does — shown, not just computed. */
  why: string;
}

/**
 * UPN comparison, case-insensitively and whitespace-trimmed.
 *
 * Entra UPNs are not case-stable, which is why the backend's access config looks
 * up the same way and why the server lower-cases what it authenticates. A
 * case-sensitive compare here would render a manager's own session as somebody
 * else's — read-only, with no way to resume it, on a Friday.
 */
export function sameManager(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = (a ?? "").trim().toLowerCase();
  const right = (b ?? "").trim().toLowerCase();
  return left !== "" && left === right;
}

/** Is this session's responsible manager the signed-in principal? */
export function isMine(session: CountSession, principal: string | null | undefined): boolean {
  return sameManager(session.responsible_manager, principal);
}

/**
 * May the caller review and post this session **without owning it**?
 *
 * The server's per-session answer (`may_post`) and nothing else - the client
 * never learns a role or a capability (D-7), only whether *this* session is one
 * the caller could post. Ownership is excluded because the owner's route is
 * decided by `mine` and has not changed; this is only the named exception to the
 * entry lock (`count/sessions.py` `_assert_owner_or_reviewer`). Entry,
 * submit-for-review and cancel stay owner-only whatever this says.
 *
 * Only `InReview` - the one status `PostSession` accepts. A reviewer looking at an
 * `Open` count has nothing to post yet, and the read-only view says so.
 */
export function mayReviewOthers(
  session: CountSession,
  principal: string | null | undefined,
): boolean {
  return (
    !isMine(session, principal) && session.status === "InReview" && session.may_post === true
  );
}

/**
 * Does staging accept line changes?
 *
 * Prefers the server's own `editable` flag and falls back to the mirrored status
 * list only if it is absent — an older backend, or a payload shape that changed.
 */
export function isEditable(session: CountSession): boolean {
  if (typeof session.editable === "boolean") return session.editable;
  return (EDITABLE_STATUSES as readonly string[]).includes(session.status);
}

/** Is this session still something a manager might be working on? */
export function isActive(session: CountSession): boolean {
  return (ACTIVE_STATUSES as readonly string[]).includes(session.status);
}

/**
 * May the signed-in principal enter counts into this session?
 *
 * Both halves, in the order the server checks them: ownership first (403), then
 * whether staging is still open to changes (409). Capability (`can_enter_counts`)
 * is deliberately **not** consulted here — it is not reported to the client, and
 * the one place it would matter is a `viewer`, of which `access.yaml` lists none
 * (decision D-7, 2026-08-19). A viewer who gets that far is refused by the
 * server and sees its words verbatim.
 */
export function mayEnter(session: CountSession, principal: string | null | undefined): boolean {
  return isMine(session, principal) && isEditable(session);
}

/**
 * Whether opening this session may create a local draft on this device.
 *
 * D2 has no draft store — the offline layer is D4 — so today this is only a
 * statement of intent with a test behind it. It is written now, and in the same
 * file as the ownership rule, because the alternative is D4 deciding it again
 * somewhere else: *a session that is not mine to enter is not mine to hold a
 * draft for*. A draft for a count someone else owns could only ever be replayed
 * into a refusal, and § 4.4 promises drafts are never silently dropped — so the
 * honest thing is not to take one.
 */
export function mayHoldDraft(
  session: CountSession,
  principal: string | null | undefined,
): boolean {
  return mayEnter(session, principal);
}

/**
 * Where this session opens, and what the button that opens it should say.
 *
 * The order of these branches is the rule (phase 3 doc § 3, S2):
 *
 * 1. **`Posting` opens straight onto S7.** There is nothing to enter and nothing
 *    to review; there is a poll to watch.
 * 2. **`Posted` also opens onto S7**, which is the screen that renders an
 *    outcome — including the posting document number, the thing that ties the
 *    count out against the ledger in BC.
 * 3. **`Error` opens onto S5, error text first.** The way back is "submit for
 *    review again", and the AL **clears the text** on that transition, so it has
 *    to be read first. This holds for a non-owner too: the text is information,
 *    and the resubmit is what ownership gates.
 * 4. **Mine and editable → entry.** Resume.
 * 5. **Not mine, `InReview`, and the server says I may post it → review.** The
 *    reviewer exception: the review screen is where the post lives, and it was
 *    unreachable for the one role that exists to use it. Entry stays locked.
 * 6. **Everything else → the read-only view.** Someone else's count, a cancelled
 *    one, and — deliberately — any status this bundle does not recognise.
 */
export function affordanceFor(
  session: CountSession,
  principal: string | null | undefined,
): SessionAffordance {
  const mine = isMine(session, principal);

  if (session.status === "Posting") {
    return {
      route: "posting",
      label: "Posting — watch it",
      readOnly: true,
      why: "This count is posting in Business Central. The posting screen polls for the outcome.",
    };
  }

  if (session.status === "Posted") {
    return {
      route: "posting",
      label: "Posted — see the outcome",
      readOnly: true,
      why: "This count has posted. The posting screen names the document it posted as.",
    };
  }

  if (session.status === "Error") {
    return {
      route: "review",
      label: "Failed — read the error",
      readOnly: !mine,
      why:
        "This count failed to post. It opens on the review screen with Business Central's " +
        "own words first, because submitting it for review again clears them.",
    };
  }

  if (mine && isEditable(session)) {
    return {
      route: "entry",
      label: "Resume this count",
      readOnly: false,
      why: "Yours, and staging still accepts changes.",
    };
  }

  if (mayReviewOthers(session, principal)) {
    return {
      route: "review",
      label: "Review and post",
      readOnly: false,
      why:
        `${session.responsible_manager} entered this count. You can review it and post it; ` +
        "entering, submitting and cancelling stay with its owner.",
    };
  }

  if (!mine) {
    return {
      route: "view",
      label: "View (read-only)",
      readOnly: true,
      why: `${session.responsible_manager} is the responsible manager for this count. You can see it, but entry locks to its owner.`,
    };
  }

  return {
    route: "view",
    label: "View (read-only)",
    readOnly: true,
    why: `Staging has frozen this session at ${session.status || "an unrecognised status"}, so there is nothing to change.`,
  };
}

/** The path a session's affordance opens. */
export function sessionPath(areaId: string, session: CountSession, route: SessionRoute): string {
  return `/areas/${encodeURIComponent(areaId)}/sessions/${encodeURIComponent(
    session.session_id,
  )}/${route}`;
}

/**
 * The active sessions that make a *new* count a concurrent one.
 *
 * Not a block — the backend permits it and so does the AL (see this file's
 * header), so a block here would be a rule only this client believes. What the
 * screen does with this is require the manager to acknowledge it: starting a
 * second count for an area is a real thing to do (a re-count after a bad one is
 * left open) and a bad accident (two half-counts, both postable), and the
 * difference is whether it was deliberate.
 */
export function startWouldBeConcurrent(sessions: CountSession[]): CountSession[] {
  return sessions.filter(isActive);
}

/**
 * Today, in Tampa, as `YYYY-MM-DD`.
 *
 * The posting date defaults to today and the manager can change it (D-4 is
 * parked for the G3 meeting, including Karla's question about a Friday count
 * entered on Saturday). Tampa explicitly rather than the device's zone: the AL
 * judges the posting window in Tampa local time, and an iPad that has travelled
 * should not silently offer a different date than the one the codeunit will
 * measure. `en-CA` because it formats as ISO, which is what the API takes.
 */
export function todayInTampa(now: Date = new Date()): string {
  return now.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

/**
 * Every route a session's current state **permits**, with the default first.
 *
 * `affordanceFor` answers "where does this session open" — the question S2's button
 * asks. This answers a different one: "may this URL be rendered at all". D5 found
 * that conflating them made **S5 unreachable for the normal flow**: a manager's own
 * `InReview` count affords entry, so a redirect to the default sent every attempt to
 * reach the review screen straight back to counting, and the "Go to review" link on
 * the entry screen could not work.
 *
 * The distinction is what a URL is for. A session in `Posting` must be *forced* onto
 * S7 — there is nothing to enter and an entry screen would refuse every keystroke.
 * But a manager who taps "Go to review" on their own open count is asking for a
 * screen that is genuinely available to them: S5 is read-only over staging, and it
 * is where posting will be confirmed. Refusing that is not safety, it is a bug.
 *
 * So: statuses the manager cannot act in permit exactly one route, and an editable
 * count of one's own permits both entry and review — defaulting to entry, because
 * that is what "resume" means.
 */
export function allowedRoutes(
  session: CountSession,
  principal: string | null | undefined,
): SessionRoute[] {
  const mine = isMine(session, principal);

  // Codeunit-owned, or already resolved: S7 renders the outcome and nothing else
  // makes sense.
  if (session.status === "Posting" || session.status === "Posted") return ["posting"];

  if (session.status === "Error") {
    // **Never entry**, for the owner as much as for anyone else. Staging does still
    // accept line changes in `Error`, so permitting entry was tempting — and wrong:
    // the AL blanks `ErrorText` on `Error -> InReview`, so the message is read or
    // lost, and a bookmarked entry URL that skipped past it would take the way back
    // while destroying the reason. D2 ruled that and D5 briefly broke it.
    //
    // ⚠ **AMENDED AT D6: the posting screen is permitted too, and review is still the
    // default.** The D2/D5 ruling was written when S7 did not exist and could not
    // show a failure; the rule it was really making is *no screen may offer the way
    // past Business Central's words without showing them first*. S5 satisfies that,
    // and S7 now satisfies it as well — it renders `error_text` verbatim, and the
    // resubmit underneath it confirms before it erases anything. Refusing it here
    // would mean a manager who watched their own post fail gets the screen yanked
    // away mid-outcome, which teaches people to distrust what they just read.
    // **Entry stays forbidden**, because entry shows the message nowhere.
    return ["review", "posting"];
  }

  if (mine && isEditable(session)) return ["entry", "review"];

  // The reviewer exception: review is where this caller's authority runs, so it
  // is the default; the read-only view stays permitted.
  if (mayReviewOthers(session, principal)) return ["review", "view"];

  // Someone else's count, or one staging has frozen. `view` is where it opens, and
  // **`review` is permitted read-only** — D-7 asks for the read-only rendering
  // rather than a separate screen, the spec puts others on a session as viewers, and
  // the review endpoint is readable in every status (pinned by
  // `tests/test_review.py`). Every action on that screen locks to the owner anyway.
  return ["view", "review"];
}
